import React, { useEffect, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import RecentActivity from './RecentActivity';
import DriverFoundNotification from './DriverFoundNotification';
import DashboardActions from './DashboardActions';
import driverApi from '../../clients/DriverClient';
import { MatchDriverResponse } from '../../models/MatchDriverResponse';
import identityApi from '../../clients/IdentityClient';
import TabControl, { Tab } from '../TabControl';
import DriverLeaderboardsView from './DriverLeaderboardsView';
import CareerView from './CareerView';
import RaceAroundWorldView from './RaceAroundWorldView';
import { DriverUsedFacility } from '../../models/RaceData';
import { currentTenant } from '../../tenant/tenants';
import EmptyState from '../EmptyState';
import { useVenueDriver } from './useVenueDriver';

// Every tab reads the brand's race-data API: CommonApi (World of Racing's data) for WOR, the venue's
// own WebApi for the other brands. Only WOR matches a driver by name and links it in Identity; a venue
// links the account to its own driver row (driver/me, driver/link).
const { raceData: hasRaceData, ratw: hasRatw, career: hasCareer } = currentTenant.features;
const isWor = currentTenant.key === 'wor';

const DriverDashboard: React.FC = () => {
  const { user, balance } = useAuth();
  const venueDriver = useVenueDriver(hasRaceData && !isWor, user?.id);
  const driverId = isWor
    ? user?.driverId || undefined
    : venueDriver.status === 'linked' ? venueDriver.driver.driverId : undefined;
  const [foundDriver, setFoundDriver] = useState<MatchDriverResponse | null>(null);
  const [hasCheckedForDriver, setHasCheckedForDriver] = useState(false);
  const [facilities, setFacilities] = useState<DriverUsedFacility[]>([]);
  const [selectedFacility, setSelectedFacility] = useState<DriverUsedFacility | null>(null);
  const [isLoadingFacilities, setIsLoadingFacilities] = useState(false);
  const [facilitiesError, setFacilitiesError] = useState('');

  useEffect(() => {
    const checkForDriver = async () => {
      if (isWor && hasRaceData && user && !user.driverId && !hasCheckedForDriver && user.firstName && user.lastName && user.email) {
        setHasCheckedForDriver(true);

        try {
          const driverMatch = await driverApi.findDriverByUserData({
            email: user.email,
            firstName: user.firstName,
            lastName: user.lastName
          });

          if (driverMatch !== null) {
            const dismissedDriverId = localStorage.getItem('dismissedDriverId');
            if (dismissedDriverId !== driverMatch.driverId) {
              setFoundDriver(driverMatch);
            }
          }
        } catch (error) {
          console.error('Error checking for driver:', error);
        }
      }
    };

    checkForDriver();
  }, [user, hasCheckedForDriver]);

  // Load facilities when driverId is available
  useEffect(() => {
    const fetchFacilities = async () => {
      if (!hasRaceData || !driverId) return;
      setIsLoadingFacilities(true);
      setFacilitiesError('');
      try {
        const result = await driverApi.getDriverUsedFacilities(driverId);
        if (result) {
          // Sort facilities by track friendly name
          const sortedFacilities = result.sort((a, b) => 
            a.trackFriendlyName.localeCompare(b.trackFriendlyName)
          );
          setFacilities(sortedFacilities);
          // Auto-select first facility if none selected
          if (sortedFacilities.length > 0) {
            setSelectedFacility((currentFacility) => currentFacility || sortedFacilities[0]);
          }
        }
      } catch (e) {
        setFacilitiesError('Failed to load facilities');
      } finally {
        setIsLoadingFacilities(false);
      }
    };
    fetchFacilities();
  }, [driverId]);

  const handleConnectDriver = async () => {
    if (!user?.id || !foundDriver?.driverId) {
      console.error('User ID is not set');
      return;
    }

    console.log('Connecting driver:', foundDriver?.driverId);
    await identityApi.linkUserToDriver({ driverId: foundDriver?.driverId, userId: user.id });
    localStorage.setItem('driverUser', JSON.stringify({ ...user, driverId: foundDriver?.driverId }));
    user.driverId = foundDriver?.driverId;

    await identityApi.refreshToken();
    localStorage.setItem('authToken', await identityApi.refreshToken());
    setFoundDriver(null);
  };

  const handleCloseNotification = () => {
    if (foundDriver?.driverId) {
      localStorage.setItem('dismissedDriverId', foundDriver.driverId);
    }
    setFoundDriver(null);
  };

  const handleFacilityChange = (facilityKey: string) => {
    const facility = facilities.find(f => `${f.track}-${f.trackLayout}-${f.car}` === facilityKey);
    setSelectedFacility(facility || null);
  };

  // Create tabs for the tab control
  const tabs: Tab[] = [
    ...(hasRatw ? [{
      id: 'race-around-world',
      title: 'Race Around the World',
      content: <RaceAroundWorldView driverId={driverId} />
    }] : []),
    {
      id: 'leaderboards',
      title: 'Leaderboards',
      content: <DriverLeaderboardsView
        driverId={driverId}
        facilities={facilities}
        selectedFacility={selectedFacility}
        isLoadingFacilities={isLoadingFacilities}
        facilitiesError={facilitiesError}
        onFacilityChange={handleFacilityChange}
      />
    },
    {
      id: 'recent-activity',
      title: 'Recent Activity',
      content: <RecentActivity driverId={driverId} take={10} />
    },
    ...(hasCareer ? [{
      id: 'career',
      title: 'Career',
      content: <CareerView driverId={driverId} />
    }] : [])
  ];

  return (
    <>
      <div className="container driver-dashboard">
        <div className="driver-info">
          <DashboardActions
            driverId={driverId}
            firstName={user?.firstName}
            preferredLocation={user?.preferredLocation}
            balance={balance}
          />
        </div>
        {!hasRaceData ? (
          <div className="driver-info">
            <h2 className="h5 mb-2">Your lap times and race history are on their way</h2>
            <p className="text-paragraphs mb-0">
              We're connecting your {currentTenant.brand.name} races to your account. Check back soon.
            </p>
          </div>
        ) : isWor ? (
          !user?.driverId && foundDriver ? (
            <DriverFoundNotification
              driverData={foundDriver}
              onConnect={handleConnectDriver}
              onClose={handleCloseNotification}
            />
          ) : (
            <TabControl tabs={tabs} defaultTabId={tabs[0].id} />
          )
        ) : venueDriver.status === 'linked' ? (
          <TabControl tabs={tabs} defaultTabId={tabs[0].id} />
        ) : venueDriver.status === 'unlinked' ? (
          <div className="driver-info">
            <p className="text-paragraphs mb-0">
              We couldn't find your races. Ask at the front desk to connect your account.
            </p>
          </div>
        ) : venueDriver.status === 'error' ? (
          <div className="driver-info" role="alert">
            <p className="text-paragraphs mb-0">We couldn't load your races right now. Please try again later.</p>
          </div>
        ) : (
          <EmptyState message="Loading your races..." animate={true} />
        )}
      </div>
    </>
  );
};

export default DriverDashboard;